import { Flex, FormControl, FormHelperText, FormLabel, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  PersonSearchCapability,
  useSetResponsiblePersonMutation,
  type PersonDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { coordinatorRegistry } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";
import { PersonSelector } from "../../../../../common/persons/PersonSelector";

interface Props {
  eventId: string;
  homeDepartmentId?: string | null;
  responsiblePerson: PersonDto;
}

export const ResponsibleBuyerField = ({ eventId, homeDepartmentId, responsiblePerson }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const [setResponsible, { isLoading }] = useSetResponsiblePersonMutation();

  const apply = async (person: PersonDto | null) => {
    if (!person || person.id === responsiblePerson.id) return;
    try {
      await coordinatorRegistry.flush(eventId);
      const updated = await setResponsible({
        eventId,
        setResponsiblePersonRequest: { responsiblePersonId: person.id },
      }).unwrap();
      coordinatorRegistry.get(eventId)?.setServerState(updated);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <FormControl mb="2">
      <Flex align="center" gap="1">
        <FormLabel mb="0">{t("Responsible buyer")}</FormLabel>
        {isLoading && <Spinner size="xs" />}
      </Flex>
      <PersonSelector
        size="md"
        organizationIds={[selectedOrg.id]}
        capability={PersonSearchCapability.ManageSourcingEvent}
        departmentId={homeDepartmentId ?? null}
        selectedPerson={responsiblePerson}
        onChange={apply}
        isClearable={false}
        isDisabled={isLoading}
      />
      <FormHelperText>{t("Only users who can create procurements are listed")}</FormHelperText>
    </FormControl>
  );
};
